import SharedCableWorkerClient from 'utils/sharedCableWorkerClient';
import FallbackCableClient from 'utils/fallbackCableClient';

let clientInstance = null;

export const WORKER_READY_TIMEOUT_MS = 3000;

/**
 * Build a resilient wrapper around SharedCableWorkerClient that
 * detects asynchronous worker failures (script load errors,
 * message-port errors, missing ready ack) and transparently swaps
 * to a FallbackCableClient, replaying any active subscriptions.
 *
 * @param  {SharedWorker} worker  The SharedWorker instance
 * @return {Object} An object exposing subscribe/unsubscribe/updateToken/disconnect
 */
function createResilientWorkerClient(worker) {
    const activeSubscriptions = {};
    let delegate;
    let hasFallenBack = false;
    let readyReceived = false;
    let readyTimer;

    function fallbackToPerTab() {
        if (hasFallenBack) return;
        hasFallenBack = true;
        clearTimeout(readyTimer);

        try {
            delegate.disconnect();
        } catch (_) {
            /* noop */
        }
        try {
            worker.port.close();
        } catch (_) {
            /* noop */
        }

        const fallback = new FallbackCableClient();
        delegate = fallback;

        Object.keys(activeSubscriptions).forEach(ticketId => {
            const { token, cbs } = activeSubscriptions[ticketId];
            fallback.subscribe(ticketId, token, cbs);
        });
    }

    // Catch worker script-load / parse errors
    worker.onerror = () => fallbackToPerTab();

    delegate = new SharedCableWorkerClient(worker);

    // Listen for the ready ack (coexists with SharedCableWorkerClient's onmessage)
    worker.port.addEventListener('message', event => {
        if (event.data && event.data.type === 'ready') {
            readyReceived = true;
        }
    });

    // Catch message-deserialization errors on the port
    worker.port.onmessageerror = () => fallbackToPerTab();

    // If the worker never acknowledges init, fall back
    readyTimer = setTimeout(() => {
        if (!readyReceived && !hasFallenBack) {
            fallbackToPerTab();
        }
    }, WORKER_READY_TIMEOUT_MS);

    return {
        subscribe(ticketId, token, cbs) {
            activeSubscriptions[ticketId] = { token, cbs };
            delegate.subscribe(ticketId, token, cbs);
        },
        unsubscribe(ticketId) {
            delete activeSubscriptions[ticketId];
            delegate.unsubscribe(ticketId);
        },
        updateToken(token) {
            Object.keys(activeSubscriptions).forEach(id => {
                activeSubscriptions[id].token = token;
            });
            delegate.updateToken(token);
        },
        disconnect() {
            clearTimeout(readyTimer);
            Object.keys(activeSubscriptions).forEach(id => {
                delete activeSubscriptions[id];
            });
            delegate.disconnect();
        },
    };
}

/**
 * Get or create the shared cable client singleton.
 * Uses SharedWorker when available, otherwise falls back to per-tab.
 *
 * @return {Object}
 */
export function getSharedCableClient() {
    if (clientInstance) return clientInstance;

    if (typeof SharedWorker !== 'undefined') {
        try {
            const worker = new SharedWorker(
                new URL('../workers/cableSharedWorker.js', import.meta.url),
                { name: 'cable-shared-worker' }
            );
            clientInstance = createResilientWorkerClient(worker);
        } catch (e) {
            clientInstance = new FallbackCableClient();
        }
    } else {
        clientInstance = new FallbackCableClient();
    }

    return clientInstance;
}

/**
 * Reset the client singleton (used on logout).
 */
export function resetSharedCableClient() {
    if (clientInstance) {
        clientInstance.disconnect();
        clientInstance = null;
    }
}

export { SharedCableWorkerClient, FallbackCableClient };
